import type { AnalyticsOption, AnalyticsQuestion, Observation } from './normalize';
import { median, pearson, quantile, spearman } from './stats';
import { parseNumberText, type QuestionSummary } from './summarize';

/** Correlations need at least this many pairwise complete responses. */
export const MIN_CORRELATION_N = 10;
/** Suggested pairs need at least this absolute Spearman rho. */
export const MIN_SUGGESTED_RHO = 0.3;
/** At most this many suggested pairs. */
const MAX_SUGGESTED_PAIRS = 3;
/** Short category lists keep author order and zero categories. */
const SHORT_LIST = 7;

/** A question read as categories or numbers for comparisons. */
export type Encoding =
  | { type: 'categorical'; categories: AnalyticsOption[]; byResponse: Map<string, string[]> }
  | { type: 'numeric'; byResponse: Map<string, number> };

/** A question with its comparison encoding and summary. */
export interface EncodedQuestion {
  question: AnalyticsQuestion;
  summary: QuestionSummary;
  encoding: Encoding;
}

/** One cross-tab cell: respondents in both categories, and the share of the row. */
export interface CrosstabCell {
  rowKey: string;
  columnKey: string;
  count: number;
  rowShare: number;
}

/** Overlapping scatter points merge; `count` sizes the mark. */
export interface ScatterPoint {
  x: number;
  y: number;
  count: number;
}

/** Numbers of one category: every value plus median, IQR and range. */
export interface Group {
  key: string;
  label: string;
  n: number;
  values: number[];
  median: number | null;
  q1: number | null;
  q3: number | null;
  min: number | null;
  max: number | null;
}

/** The view a pair of questions produces. `n` counts respondents with both answers. */
export type PairComparison =
  | { kind: 'crosstab'; a: EncodedQuestion; b: EncodedQuestion; n: number; rows: AnalyticsOption[]; columns: AnalyticsOption[]; rowTotals: number[]; cells: CrosstabCell[][] }
  | { kind: 'scatter'; a: EncodedQuestion; b: EncodedQuestion; n: number; points: ScatterPoint[]; pearson: number | null; spearman: number | null }
  | { kind: 'groups'; category: EncodedQuestion; value: EncodedQuestion; n: number; groups: Group[] };

/** One cell of the correlation matrix, by field id. */
export interface CorrelationCell {
  a: string;
  b: string;
  n: number;
  pearson: number | null;
  spearman: number | null;
}

/** A suggested numeric pair. */
export interface SuggestedPair {
  a: EncodedQuestion;
  b: EncodedQuestion;
  n: number;
  pearson: number | null;
  spearman: number | null;
}

/** Categories in display order: author order for short lists, used categories by count otherwise. */
function orderCategories(categories: AnalyticsOption[], byResponse: Map<string, string[]>): AnalyticsOption[] {
  if (categories.length <= SHORT_LIST) return categories;
  const counts = new Map(categories.map(option => [option.key, 0]));
  for (const keys of byResponse.values()) for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1);
  return categories.map((option, index) => ({ option, index, count: counts.get(option.key) ?? 0 }))
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .map(item => item.option);
}

/**
 * Encode a question for comparisons from the same observations and summary
 * the card uses: choices, checkboxes, countries and few-distinct text are
 * categorical; numbers, ratings and text read as a number are numeric.
 */
export function encodeQuestion(question: AnalyticsQuestion, observations: readonly Observation[], summary: QuestionSummary): EncodedQuestion | null {
  if (summary.kind === 'numeric' || summary.kind === 'nps') {
    const byResponse = new Map<string, number>();
    for (const entry of observations) {
      if (entry.status !== 'answered') continue;
      const value = entry.value.kind === 'number' ? entry.value.value : entry.value.kind === 'text' ? parseNumberText(entry.value.value) : null;
      if (value !== null) byResponse.set(entry.responseId, value);
    }
    return { question, summary, encoding: { type: 'numeric', byResponse } };
  }
  if (summary.kind !== 'distribution') return null;
  const byResponse = new Map<string, string[]>();
  for (const entry of observations) {
    if (entry.status !== 'answered') continue;
    const value = entry.value;
    if (value.kind === 'choice') byResponse.set(entry.responseId, value.keys);
    else if (value.kind === 'boolean') byResponse.set(entry.responseId, [String(value.value)]);
    else if (value.kind === 'address') byResponse.set(entry.responseId, [value.country]);
    else if (value.kind === 'text') byResponse.set(entry.responseId, [value.value.normalize('NFC').toLocaleLowerCase()]);
  }
  const categories = summary.source === 'choice' ? question.options
    : summary.source === 'boolean' ? [{ key: 'true', label: 'Checked' }, { key: 'false', label: 'Not checked' }]
      : summary.items.map(item => ({ key: item.key, label: item.label }));
  return { question, summary, encoding: { type: 'categorical', categories: orderCategories(categories, byResponse), byResponse } };
}

/** Pairwise complete numeric values, in the first question's response order. */
function pairs(a: Map<string, number>, b: Map<string, number>): { xs: number[]; ys: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [id, x] of a) {
    const y = b.get(id);
    if (y !== undefined) {
      xs.push(x);
      ys.push(y);
    }
  }
  return { xs, ys };
}

/** Pearson and Spearman with n, withheld below the correlation minimum. */
function correlation(a: Map<string, number>, b: Map<string, number>): { n: number; pearson: number | null; spearman: number | null } {
  const { xs, ys } = pairs(a, b);
  const enough = xs.length >= MIN_CORRELATION_N;
  return { n: xs.length, pearson: enough ? pearson(xs, ys) : null, spearman: enough ? spearman(xs, ys) : null };
}

/** Numbers of each category in category order, zero groups included. */
function groupSpread(category: EncodedQuestion, value: EncodedQuestion): PairComparison {
  const categorical = category.encoding as Extract<Encoding, { type: 'categorical' }>;
  const numeric = value.encoding as Extract<Encoding, { type: 'numeric' }>;
  const respondents = new Set<string>();
  const groups = categorical.categories.map(option => {
    const values: number[] = [];
    for (const [id, keys] of categorical.byResponse) {
      const number = numeric.byResponse.get(id);
      if (number === undefined || !keys.includes(option.key)) continue;
      values.push(number);
      respondents.add(id);
    }
    values.sort((x, y) => x - y);
    return {
      key: option.key,
      label: option.label,
      n: values.length,
      values,
      median: median(values),
      q1: quantile(values, 0.25),
      q3: quantile(values, 0.75),
      min: values.length ? values[0] : null,
      max: values.length ? values[values.length - 1] : null,
    };
  });
  return { kind: 'groups', category, value, n: respondents.size, groups };
}

/** The view for two questions: cross-tab, scatter, or per-group spread. */
export function comparePair(a: EncodedQuestion, b: EncodedQuestion): PairComparison {
  if (a.encoding.type === 'numeric' && b.encoding.type === 'numeric') {
    const { xs, ys } = pairs(a.encoding.byResponse, b.encoding.byResponse);
    const merged = new Map<string, ScatterPoint>();
    xs.forEach((x, index) => {
      const key = `${x}\u0000${ys[index]}`;
      const point = merged.get(key) ?? { x, y: ys[index], count: 0 };
      point.count++;
      merged.set(key, point);
    });
    const points = [...merged.values()].sort((p, q) => p.x - q.x || p.y - q.y);
    return { kind: 'scatter', a, b, points, ...correlation(a.encoding.byResponse, b.encoding.byResponse) };
  }
  if (a.encoding.type === 'categorical' && b.encoding.type === 'categorical') {
    const rows = a.encoding.categories;
    const columns = b.encoding.categories;
    const both = [...a.encoding.byResponse].flatMap(([id, keys]) => {
      const other = (b.encoding as Extract<Encoding, { type: 'categorical' }>).byResponse.get(id);
      return other ? [{ keys, other }] : [];
    });
    const rowTotals = rows.map(row => both.filter(entry => entry.keys.includes(row.key)).length);
    const cells = rows.map((row, rowIndex) => columns.map(column => {
      const count = both.filter(entry => entry.keys.includes(row.key) && entry.other.includes(column.key)).length;
      return { rowKey: row.key, columnKey: column.key, count, rowShare: rowTotals[rowIndex] ? count / rowTotals[rowIndex] : 0 };
    }));
    return { kind: 'crosstab', a, b, n: both.length, rows, columns, rowTotals, cells };
  }
  return a.encoding.type === 'categorical' ? groupSpread(a, b) : groupSpread(b, a);
}

/** Spearman (and Pearson) for every numeric pair, blank below n = 10. */
export function correlationMatrix(numeric: readonly EncodedQuestion[]): { questions: EncodedQuestion[]; cells: CorrelationCell[] } {
  const questions = numeric.filter(item => item.encoding.type === 'numeric');
  const cells: CorrelationCell[] = [];
  questions.forEach((a, i) => questions.slice(i + 1).forEach(b => {
    const result = correlation(
      (a.encoding as Extract<Encoding, { type: 'numeric' }>).byResponse,
      (b.encoding as Extract<Encoding, { type: 'numeric' }>).byResponse,
    );
    cells.push({ a: a.question.id, b: b.question.id, ...result });
  }));
  return { questions, cells };
}

/**
 * Up to 3 numeric pairs ranked by |Spearman rho|, each with n >= 10 and
 * |rho| >= 0.3. The thresholds limit chance correlations among many pairs.
 */
export function suggestPairs(numeric: readonly EncodedQuestion[]): SuggestedPair[] {
  const { questions, cells } = correlationMatrix(numeric);
  const byId = new Map(questions.map(item => [item.question.id, item]));
  return cells
    .map((cell, index) => ({ cell, index }))
    .filter(({ cell }) => cell.n >= MIN_CORRELATION_N && cell.spearman !== null && Math.abs(cell.spearman) >= MIN_SUGGESTED_RHO)
    .sort((p, q) => Math.abs(q.cell.spearman!) - Math.abs(p.cell.spearman!) || p.index - q.index)
    .slice(0, MAX_SUGGESTED_PAIRS)
    .map(({ cell }) => ({ a: byId.get(cell.a)!, b: byId.get(cell.b)!, n: cell.n, pearson: cell.pearson, spearman: cell.spearman }));
}
